import { Seo } from "@/components/Seo";
import { ArPageHero, ArBlock, ArWhatHappensNext, ArContactCTA } from "@/pages/ar/_ArShared";

const STAGES = [
  { n: "01", h: "الاكتشاف", meta: "مجاني، من 30 دقيقة إلى أسبوع",
    body: "مكالمة مع مهندس. نسأل عن مشكلة العمل، لا عن قائمة الميزات. تحصلون على ملخص من صفحة واحدة: ما فهمناه، المخاطر التي نراها، النهج المقترح، ونطاق التكلفة. الملخص لكم، ويمكنكم أخذه لمورد آخر." },
  { n: "02", h: "تحديد النطاق", meta: "أسبوع إلى أسبوعين",
    body: "تُقسَّم الأعمال إلى وحدات، كل واحدة بسعرها. سعر ثابت للنطاق المحدد · وقت ومواد بحد أقصى لكل مرحلة · فريق مخصص لثلاثة أشهر فأكثر." },
  { n: "03", h: "التصميم", meta: "أسبوعان إلى ثلاثة",
    body: "إطارات أولية، ثم شاشات نهائية بالعربية والإنجليزية. تُشاهدون نموذجًا يعمل قبل كتابة أي كود. تغيير شاشة يكلف ساعة؛ تغيير ميزة مبنية يكلف أسبوعًا." },
  { n: "04", h: "البناء", meta: "مراحل كل أسبوعين",
    body: "برمجيات تعمل في نهاية كل مرحلة، لا تقرير حالة. طلبات التغيير تُسعَّر قبل بنائها. أوقفوا أو غيّروا الاتجاه عند أي حد مرحلة." },
  { n: "05", h: "الاختبار", meta: "مستمر، مع بوابة إصدار",
    body: "اختبار وظيفي، أداء، أمان، وأجهزة حقيقية. لا شيء يصل للإنتاج دون اجتياز اختبار موقّع." },
  { n: "06", h: "الإطلاق", meta: "نشر ومراقبة",
    body: "نشر، مراقبة، تتبع الأخطاء، وخطة تراجع، مع بقائنا في حالة استعداد لأول 72 ساعة. تستلمون الكود المصدري، التوثيق، المخططات، الأدلة التشغيلية، وبيانات الدخول. البرمجيات ملككم." },
  { n: "07", h: "التشغيل", meta: "مستمر بموجب اتفاقية",
    body: "ثلاث باقات دعم — أساسي، أعمال، حرج — أو تسليم نظيف بضمان 90 يومًا وأسبوعين لنقل المعرفة." },
];

const TIERS = [
  { title: "أساسي", desc: "دعم عمل ثمان ساعات، رد خلال يوم عمل، تحديثات أمنية." },
  { title: "أعمال", desc: "دعم موسّع، رد خلال ساعتين لأولوية عالية، مراقبة استباقية." },
  { title: "حرج", desc: "دعم 24/7، رد خلال 30 دقيقة، مهندس دائم على الاستعداد." },
];

export default function HowWeWorkAr() {
  return (
    <div data-testid="how-we-work-ar">
      <Seo title="كيف نعمل" description="من الاكتشاف إلى التشغيل — سبع مراحل، مراحل كل أسبوعين، وتوقف عند أي مرحلة." />
      <ArPageHero
        eyebrow="كيف نعمل"
        h1="كيف نعمل"
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "الشركة", to: "/ar/company" }, { label: "كيف نعمل" }]}
      />

      <ArBlock tint="cream" heading="معظم المشاريع تفشل بهدوء، ثم دفعة واحدة.">
        <div className="max-w-4xl space-y-4 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          <p>النمط يتكرر دائمًا. يُتفق على النطاق شفهيًا. لا شيء مرئي لأربعة أشهر. وعندما يرى أحد أخيرًا برمجيات تعمل، تكون الميزانية قد استُنفدت والمتطلبات قد تغيّرت.</p>
          <p>كل خطوة أدناه موجودة لأننا رأينا ذلك يحدث في مكان ما وقررنا تصميمه بعيدًا عن مشاريعنا.</p>
        </div>
      </ArBlock>

      <ArBlock tint="white">
        <div className="space-y-8">
          {STAGES.map((s, i) => (
            <div key={i} className="grid grid-cols-1 gap-6 border-b border-wafa-deep/10 pb-8 last:border-b-0 lg:grid-cols-12">
              <div className="lg:col-span-3">
                <div className="font-heading text-3xl font-bold text-wafa-primary">{s.n}</div>
                <div className="mt-2 font-heading text-xl font-semibold text-wafa-deep">{s.h}</div>
                <div className="mt-1 text-xs text-wafa-ink/60">{s.meta}</div>
              </div>
              <div className="lg:col-span-9">
                <p className="text-base leading-relaxed text-wafa-ink/85">{s.body}</p>
                {i === 6 ? (
                  <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {TIERS.map((t, j) => (
                      <div key={j} className="rounded-md bg-wafa-cream/50 p-5">
                        <div className="font-heading text-sm font-semibold uppercase tracking-wider text-wafa-deep">{t.title}</div>
                        <p className="mt-2 text-sm leading-relaxed text-wafa-ink/80">{t.desc}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </ArBlock>

      <ArBlock tint="cream" heading="ما نحتاجه منكم">
        <p className="max-w-4xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          إجابات صادقة عن العمل. صاحب قرار واحد يمكنه الموافقة. ملاحظات خلال ثلاثة أيام عمل. والوصول لمن يعرف فعليًا كيف تسير العملية الحالية — هذا الأخير أهم من كل ما سبق مجتمعًا.
        </p>
      </ArBlock>

      <ArWhatHappensNext />
      <ArContactCTA heading="جاهزون للبدء؟ احجزوا مكالمة استكشافية." />
    </div>
  );
}
